// src/pages/EditProfile.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Form, Button, Alert, Row, Col } from "react-bootstrap";
import { FaUserCircle } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";

const defaultProfileImage = "https://via.placeholder.com/150?text=User";

const EditProfile = () => {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const mustChangePassword = !!user?.forcePasswordChange;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [contactNo, setContactNo] = useState("");
  const [address, setAddress] = useState("");


  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Helper to get the correct profile picture URL
  const getProfilePicUrl = (profilePicture) => {
    if (!profilePicture) return defaultProfileImage;
    if (profilePicture.startsWith('http')) return profilePicture;
    return `${process.env.REACT_APP_FILE_BASE_URL}/${profilePicture.replace(/\\/g, '/')}`;
  };

  // Fetch user data from the GET endpoint and set fields
  useEffect(() => {
    // Autofill email from logged-in user
    if (user && user.email) {
      setEmail(user.email);
    }
    const fetchUserData = async () => {
      try {
         if (!user || !user.id) return;
        const res = await axios.get(`/api/users/${user.id}`);
        const data = res.data;
        if (data.fullName) {
          const names = data.fullName.split(' ');
          setFirstName(names[0]);
          setLastName(names.slice(1).join(' '));
        } else if (data.firstName && data.lastName) {
          setFirstName(data.firstName);
          setLastName(data.lastName);
        } else if (data.name) {
          const names = data.name.split(" ");
          setFirstName(names[0]);
          setLastName(names.slice(1).join(" "));
        }
        setProfilePicturePreview(getProfilePicUrl(data.profilePicture));
        setContactNo(data.contactNo || "");
        setAddress(data.address || "");
      } catch (err) {
        setError("Failed to fetch user data.");
        console.error("Error fetching user data:", err);
      }
    };
    fetchUserData();
    // eslint-disable-next-line
  }, [user]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setProfilePicture(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setProfilePicturePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      // If no file is selected, revert to backend image
      setProfilePicturePreview(getProfilePicUrl(user && user.profilePicture));
    }
  };

  const handlePictureClick = () => {
    document.getElementById('profilePictureInput').click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (mustChangePassword && !newPassword) {
      setError("You must set a new password before you can continue.");
      return;
    }
    if ((newPassword || confirmNewPassword) && !currentPassword) {
      setError("Please enter your current password to update your password.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError("New password and confirmation do not match.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      // Roles/userCategory are deliberately omitted - this is a self-service
      // profile edit, and the backend no longer touches roles from this
      // payload at all (role changes are admin-only). Sending them from here
      // is also unreliable: `user` often has no `roles` array populated yet
      // (e.g. right after login), so it would just be sending stale/empty data.
      const userObj = {
        username: user.username,
        email,
        fullName: `${firstName} ${lastName}`.trim(),
        contactNo,
        address
      };
      console.log('User object being sent:', userObj);
      formData.append('user', new Blob([JSON.stringify(userObj)], { type: 'application/json' }));
      if (currentPassword) formData.append('currentPassword', currentPassword);
      if (newPassword) formData.append('newPassword', newPassword);
      if (profilePicture) formData.append('profilePicture', profilePicture);

      const response = await axios.put(`/api/users/${user.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
    });

      const updatedUser = response.data;
      if (setUser) setUser({ ...user, ...updatedUser });
      setSuccessMessage('Profile updated successfully.');
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update profile.");
      console.error("Error updating profile:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-5 pt-5">
      <h2 className="mb-4">Edit Profile</h2>
      {mustChangePassword && (
        <Alert variant="warning">
          You're using a password an administrator set for you. You must choose a new one before you can continue.
        </Alert>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      {/* Profile picture at the top */}
      <div className="text-center mb-4">
        <div
          onClick={handlePictureClick}
          style={{ cursor: "pointer", display: "inline-block" }}
          title="Click to upload a new profile picture">
          {profilePicturePreview ? (
            <img
              src={profilePicturePreview}
              alt="Profile"
              style={{
                width: "150px",
                height: "150px",
                objectFit: "cover",
                borderRadius: "50%",
                border: "2px solid #ccc",
              }}
            />
          ) : (
            <FaUserCircle style={{ fontSize: "150px", color: "#ccc" }} />
          )}
          <input
            type="file"
            id="profilePictureInput"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </div>
      </div>

      <Form onSubmit={handleSubmit} autoComplete="off">
        <Row>
          <Col md={6}>
            <Form.Group controlId="formFirstName" className="mb-3">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formLastName" className="mb-3">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </Form.Group>
          </Col>
        </Row>
        <Form.Group controlId="formEmail" className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control type="email" value={email} readOnly />
        </Form.Group>

        <Form.Group controlId="formContactNo" className="mb-3">
          <Form.Label>Contact Number</Form.Label>
          <Form.Control type="tel" value={contactNo}
            onChange={(e) => setContactNo(e.target.value)}
          />
        </Form.Group>

        <Form.Group controlId="formAddress" className="mb-3">
          <Form.Label>Address</Form.Label>
          <Form.Control as="textarea" rows={2} value={address}
            onChange={(e) => setAddress(e.target.value)}
         />
        </Form.Group>

        <h4 className="mt-4">Update Password</h4>
        <Form.Group controlId="formCurrentPassword" className="mb-3">
          <Form.Label>Current Password</Form.Label>
          <Form.Control
            type="password"
            placeholder="Enter your current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            required={mustChangePassword}
          />
        </Form.Group>
        <Form.Group controlId="formNewPassword" className="mb-3">
          <Form.Label>New Password</Form.Label>
          <Form.Control
            type="password"
            placeholder="Enter a new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            required={mustChangePassword}
          />
        </Form.Group>
        <Form.Group controlId="formConfirmNewPassword" className="mb-3">
          <Form.Label>Confirm New Password</Form.Label>
          <Form.Control
            type="password"
            placeholder="Confirm new password"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            required={mustChangePassword}
          />
        </Form.Group>
        <Button variant="primary" type="submit" disabled={loading}>
          {loading ? "Updating..." : "Update Profile"}
        </Button>
      </Form>
    </Container>
  );
};

export default EditProfile;
